// @ts-nocheck — type-checked by the ui-components vitest suite; the frontend
// tsc build cannot resolve react/jsx-runtime from this out-of-tree location.
/**
 * VerticalTimeline — user-visible action timeline for escalation governance.
 *
 * Shows chronological events with governance metadata (PDP decisions,
 * approval status, config hashes). Supports first-escalation walkthrough
 * annotations that explain each governance step in plain language.
 */

const React = (globalThis as any).React;
const { useState } = React;

export interface TimelineEventData {
  eventId: string;
  timestamp: string;
  eventType: string;
  title: string;
  description?: string;
  actorType: 'agent' | 'user' | 'system';
  actorId?: string;
  operationId?: string;
  systemKey?: string;
  pdpDecision?: string;
  approvalStatus?: string;
  statusCode?: number;
  configHash?: string;
  tokenRefreshed?: boolean;
  annotation?: string;
}

export interface VerticalTimelineProps {
  events: TimelineEventData[];
  showAnnotations?: boolean;
  onToggleAnnotations?: () => void;
  loading?: boolean;
  emptyMessage?: string;
}

const EVENT_TYPE_COLORS: Record<string, string> = {
  escalation_detected: 'var(--color-warning, #faad14)',
  context_built: 'var(--color-info, #1890ff)',
  plan_created: 'var(--color-info, #1890ff)',
  action_executed: 'var(--color-success, #52c41a)',
  action_failed: 'var(--color-error, #ff4d4f)',
  approval_required: 'var(--color-warning, #faad14)',
  approval_granted: 'var(--color-success, #52c41a)',
  approval_denied: 'var(--color-error, #ff4d4f)',
};

const ACTOR_ICONS: Record<string, string> = {
  agent: '🤖',
  user: '👤',
  system: '⚙',
};

function formatTime(ts: string): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return ts;
  }
}

export const VerticalTimeline: React.FC<VerticalTimelineProps> = ({
  events,
  showAnnotations = false,
  onToggleAnnotations,
  loading = false,
  emptyMessage = 'No actions yet. When your agent executes its first governed action, every step will appear here with full governance detail.',
}) => {
  if (loading) {
    return (
      <div
        style={{
          padding: 'var(--spacing-lg, 24px)',
          textAlign: 'center',
          color: 'var(--color-text-secondary, #8c8c8c)',
        }}
      >
        Loading timeline...
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--spacing-lg, 24px)',
          textAlign: 'center',
          color: 'var(--color-text-secondary, #8c8c8c)',
          backgroundColor: 'var(--color-bg-layout, #fafafa)',
          borderRadius: 'var(--border-radius-lg, 8px)',
          border: '1px dashed var(--color-border, #d9d9d9)',
        }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        fontFamily: 'var(--font-family, inherit)',
      }}
      role="list"
      aria-label="Action timeline"
    >
      {onToggleAnnotations && (
        <div style={{ marginBottom: 'var(--spacing-sm, 12px)', textAlign: 'right' }}>
          <button
            onClick={onToggleAnnotations}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-primary, #1890ff)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm, 12px)',
              padding: 0,
            }}
          >
            {showAnnotations ? 'Hide explanations' : 'Explain this timeline'}
          </button>
        </div>
      )}

      {events.map((event, index) => {
        const dotColor = EVENT_TYPE_COLORS[event.eventType] || 'var(--color-text-tertiary, #8c8c8c)';
        const isLast = index === events.length - 1;

        return (
          <div
            key={event.eventId}
            role="listitem"
            style={{
              display: 'flex',
              gap: 12,
              paddingBottom: isLast ? 0 : 16,
              position: 'relative',
            }}
          >
            {/* Timeline line + dot */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: 20,
              }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: dotColor,
                  flexShrink: 0,
                  marginTop: 4,
                }}
                aria-hidden="true"
              />
              {!isLast && (
                <div
                  style={{
                    width: 2,
                    flex: 1,
                    backgroundColor: 'var(--color-border, #d9d9d9)',
                    marginTop: 4,
                  }}
                  aria-hidden="true"
                />
              )}
            </div>

            {/* Content */}
            <div style={{ flex: 1, paddingBottom: 4 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--font-size-sm, 12px)',
                    color: 'var(--color-text-secondary, #8c8c8c)',
                    minWidth: 60,
                  }}
                >
                  {formatTime(event.timestamp)}
                </span>
                <span style={{ fontWeight: 500, color: 'var(--color-text, #262626)' }}>
                  {event.title}
                </span>
              </div>

              {/* Governance badges */}
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  marginTop: 4,
                  flexWrap: 'wrap',
                }}
              >
                {event.pdpDecision && (
                  <span
                    style={{
                      fontSize: 11,
                      padding: '1px 6px',
                      borderRadius: 3,
                      backgroundColor: event.pdpDecision === 'permit'
                        ? 'var(--color-success-bg, #f6ffed)'
                        : 'var(--color-error-bg, #fff2f0)',
                      color: event.pdpDecision === 'permit'
                        ? 'var(--color-success, #52c41a)'
                        : 'var(--color-error, #ff4d4f)',
                    }}
                  >
                    PDP: {event.pdpDecision}
                  </span>
                )}
                {event.approvalStatus && (
                  <span
                    style={{
                      fontSize: 11,
                      padding: '1px 6px',
                      borderRadius: 3,
                      backgroundColor: 'var(--color-warning-bg, #fffbe6)',
                      color: 'var(--color-warning, #faad14)',
                    }}
                  >
                    {event.approvalStatus}
                  </span>
                )}
                {event.tokenRefreshed && (
                  <span
                    style={{
                      fontSize: 11,
                      padding: '1px 6px',
                      borderRadius: 3,
                      backgroundColor: 'var(--color-info-bg, #e6f7ff)',
                      color: 'var(--color-info, #1890ff)',
                    }}
                  >
                    token refreshed
                  </span>
                )}
              </div>

              {event.description && (
                <div
                  style={{
                    fontSize: 'var(--font-size-sm, 12px)',
                    color: 'var(--color-text-secondary, #8c8c8c)',
                    marginTop: 2,
                  }}
                >
                  {event.description}
                </div>
              )}

              {/* First-escalation walkthrough annotation */}
              {showAnnotations && event.annotation && (
                <div
                  style={{
                    marginTop: 6,
                    padding: '6px 10px',
                    backgroundColor: 'var(--color-primary-bg, #e6f7ff)',
                    borderLeft: '3px solid var(--color-primary, #1890ff)',
                    borderRadius: '0 var(--border-radius, 6px) var(--border-radius, 6px) 0',
                    fontSize: 'var(--font-size-sm, 12px)',
                    color: 'var(--color-text, #262626)',
                  }}
                  role="note"
                >
                  {event.annotation}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
